/**
 * DSCContext.jsx
 *
 * React context for the Drill String Configurator (DSC). Holds:
 *   - productLine ('TH' | 'DTH')
 *   - toggles (coupling / bitAdapter for TH; unitImperial for DTH; future
 *     keys for shock absorbers, stabilisers, sampler tubes — brainstorm DTH #4)
 *   - selections (per-slot picks)
 *   - dropdownChoices (intermediate dropdown values per slot)
 *   - dropdownOptions (computed by the cascade reducer)
 *   - dscFilteringData + dscDTHHammerData (master lists from dscFilteringData.js)
 *
 * State pattern is modelled on `src/contexts/WizardContext.jsx` (Group D)
 * specifically lines 71-95 (sessionStorage helpers) and lines 99-118 (URL
 * hydration). DSC has its OWN context with a DIFFERENT model — there is
 * NO `currentStep`, NO `entryPath`, NO progress sequence; DSC is
 * single-page-with-cascade per brainstorm UX paradigm §1.1.
 *
 * sessionStorage key: `mrt.dsc.state` (locked by plan §0 + §SP1.1; also
 * consumed by dscAddService.js so PDP "Add to DSC" lands here).
 *
 * URL params recognised (plan §1.2 / §SP1.1):
 *   ?type=top-hammer | dth                 → productLine
 *   TH:  ?couplings=0|1 ?bit_adapter=0|1
 *        ?mfr=... ?model=... ?coupling=... ?rod_thread=... ?bit_type=...
 *   DTH: ?unit=metric|imperial
 *        ?holesize=... ?hammer_family=... ?hammer_size=... ?shank=... ?pin=...
 *
 * Cascade reducer is the pure function in `services/dscCascade.js` — DSCContext
 * is a thin React shell that wraps it with sessionStorage persistence and
 * URL-param sync.
 *
 * @module contexts/DSCContext
 * @since 2026-05-20 (Group E SP1.1)
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';

import {
  dscCascadeReducer,
  DSC_ACTIONS,
  makeInitialDSCState,
} from '@services/dscCascade';
import { buildDSCBootstrap } from '@services/dscFilteringData';
import { computeTabCounts } from '@services/dscTabFilter';
import { getSlotOrder, getTabOrder } from '@data/dscSlotConfig';

// ============================================================================
// Constants
// ============================================================================

const SESSION_STORAGE_KEY = 'mrt.dsc.state';

// ============================================================================
// sessionStorage helpers — mirror WizardContext.jsx:75-95 pattern.
// ============================================================================

function readPersistedState() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersistedState(state) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    // Persist only the portable subset; master lists + products are re-derivable
    // from the JSON fixtures via DSCContext bootstrap.
    const subset = {
      productLine: state.productLine,
      toggles: state.toggles,
      selections: state.selections,
      dropdownChoices: state.dropdownChoices,
    };
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(subset));
  } catch {
    // Quota / privacy mode — ignore.
  }
}

// ============================================================================
// URL parameter hydration — mirror WizardContext.jsx:99-118.
// ============================================================================

/**
 * Parse the URL search string into a partial DSC state.
 * @param {string|null|undefined} search
 * @returns {Object|null}
 */
export function parseUrlHydration(search) {
  if (!search || typeof search !== 'string') return null;
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const typeRaw = params.get('type');
    if (!typeRaw) return null;
    const type = String(typeRaw).toLowerCase();
    if (type !== 'top-hammer' && type !== 'dth') return null;
    const productLine = type === 'top-hammer' ? 'TH' : 'DTH';

    const out = { productLine, toggles: {}, selections: {} };

    if (productLine === 'TH') {
      out.toggles.coupling = params.get('couplings') === '1';
      out.toggles.bitAdapter = params.get('bit_adapter') === '1';
      const mfr = params.get('mfr');
      const model = params.get('model');
      if (mfr || model) out.selections['shank-adapter'] = { mfr, model };
      const coupling = params.get('coupling');
      const rodThread = params.get('rod_thread');
      if (coupling || rodThread) {
        out.selections.couplings = {
          type: coupling || null,
          rodThread: rodThread || null,
        };
      }
      const bitThread = params.get('bit_thread');
      if (bitThread) out.selections.rod = { thread: bitThread };
      const bitType = params.get('bit_type');
      if (bitType) out.selections['drill-bit'] = { type: bitType };
      const adapterThread = params.get('adapter_thread');
      if (adapterThread) out.selections['bit-adapter'] = { thread: adapterThread };
    } else {
      out.toggles.unitImperial = params.get('unit') === 'imperial';
      const holeSize = params.get('holesize');
      if (holeSize) out.selections['dth-bit'] = { holeSize };
      const family = params.get('hammer_family');
      const size = params.get('hammer_size');
      const shank = params.get('shank');
      if (family || size || shank) {
        out.selections['hammer-series'] = {
          family: family || null,
          size: size || null,
          shank: shank || null,
        };
      }
      const pin = params.get('pin');
      if (pin) out.selections.drillpipe = { pinOption: pin };
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Serialise a state subset back into a URL query string.
 *
 * @param {Object} state
 * @returns {string}  e.g. "type=top-hammer&couplings=1&mfr=Sandvik"
 */
export function buildUrlFromState(state) {
  const params = new URLSearchParams();
  if (!state || !state.productLine) return '';

  if (state.productLine === 'TH') {
    params.set('type', 'top-hammer');
    if (state.toggles?.coupling) params.set('couplings', '1');
    if (state.toggles?.bitAdapter) params.set('bit_adapter', '1');
    const shank = state.selections?.['shank-adapter'];
    if (shank?.mfr) params.set('mfr', shank.mfr);
    if (shank?.model) params.set('model', shank.model);
    const c = state.selections?.couplings;
    if (c?.type) params.set('coupling', c.type);
    if (c?.rodThread) params.set('rod_thread', c.rodThread);
    const rod = state.selections?.rod;
    if (rod?.thread) params.set('bit_thread', rod.thread);
    const adapter = state.selections?.['bit-adapter'];
    if (adapter?.thread) params.set('adapter_thread', adapter.thread);
    const bit = state.selections?.['drill-bit'];
    if (bit?.type) params.set('bit_type', bit.type);
  } else {
    params.set('type', 'dth');
    if (state.toggles?.unitImperial) params.set('unit', 'imperial');
    const b = state.selections?.['dth-bit'];
    if (b?.holeSize) params.set('holesize', b.holeSize);
    const h = state.selections?.['hammer-series'];
    if (h?.family) params.set('hammer_family', h.family);
    if (h?.size) params.set('hammer_size', h.size);
    if (h?.shank) params.set('shank', h.shank);
    const dp = state.selections?.drillpipe;
    if (dp?.pinOption) params.set('pin', dp.pinOption);
  }
  return params.toString();
}

// ============================================================================
// Context
// ============================================================================

const DSCContext = createContext(null);

/**
 * Hook to consume DSCContext. Throws if used outside provider.
 * @returns {Object}
 */
export function useDSC() {
  const ctx = useContext(DSCContext);
  if (!ctx) throw new Error('useDSC must be used within DSCProvider');
  return ctx;
}

/**
 * Per-slot subscriber hook. Returns the slot's selection, dropdownChoices,
 * isDisabled flag (based on upstream slot completion), isFilled, and the
 * available dropdown options.
 *
 * @param {string} slotId
 * @returns {{ value: any, choices: Object, isDisabled: boolean,
 *             isFilled: boolean, options: Array, setSelection: Function,
 *             setDropdownChoice: Function, clear: Function }}
 */
export function useDSCSlot(slotId) {
  const ctx = useDSC();
  const value = ctx.state.selections[slotId];
  const choices = ctx.state.dropdownChoices[slotId];
  const isFilled = !!value && Object.values(value).some((v) => v != null);

  // Slot is disabled when its predecessor in the slot order is not filled.
  // Exception: the first slot (shank-adapter / dth-bit-first-entry / hammer-first-entry)
  // is never disabled.
  const slotOrder = getSlotOrder(ctx.state.productLine, ctx.state.toggles);
  const idx = slotOrder.indexOf(slotId);
  let isDisabled = false;
  if (idx > 0) {
    const prevId = slotOrder[idx - 1];
    const prevSel = ctx.state.selections[prevId];
    isDisabled = !prevSel;
  }

  // Convenience setters bound to this slot.
  const setSelection = useCallback(
    (dropdownKey, val) =>
      ctx.dispatch({
        type: DSC_ACTIONS.SET_SELECTION,
        slotId,
        dropdownKey,
        value: val,
      }),
    [ctx, slotId]
  );
  const setDropdownChoice = useCallback(
    (dropdownKey, val) =>
      ctx.dispatch({
        type: DSC_ACTIONS.SET_DROPDOWN_CHOICE,
        slotId,
        dropdownKey,
        value: val,
      }),
    [ctx, slotId]
  );
  const clear = useCallback(
    () => ctx.dispatch({ type: DSC_ACTIONS.CLEAR_SELECTION, slotId }),
    [ctx, slotId]
  );

  // Gather all dropdown-option entries keyed `<slotId>:<dropdownKey>` for
  // this slot — the host renders one SearchableDropdown per entry.
  const options = useMemo(() => {
    const opts = {};
    for (const [key, val] of Object.entries(ctx.state.dropdownOptions || {})) {
      if (key.startsWith(`${slotId}:`)) {
        opts[key.slice(slotId.length + 1)] = val;
      }
    }
    return opts;
  }, [ctx.state.dropdownOptions, slotId]);

  return { value, choices, isDisabled, isFilled, options, setSelection, setDropdownChoice, clear };
}

/**
 * Per-toggle subscriber hook.
 * @param {string} toggleKey
 * @returns {{ value: boolean, setValue: (b:boolean)=>void }}
 */
export function useDSCToggle(toggleKey) {
  const ctx = useDSC();
  const value = !!ctx.state.toggles?.[toggleKey];
  const setValue = useCallback(
    (b) => ctx.dispatch({ type: DSC_ACTIONS.TOGGLE, toggleKey, value: !!b }),
    [ctx, toggleKey]
  );
  return { value, setValue };
}

// ============================================================================
// Provider
// ============================================================================

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {'TH'|'DTH'} props.productLine
 * @param {string} [props.initialUrlSearch]  - For URL-param hydration on mount.
 * @param {Object[]} [props.products]        - Product array (TH or DTH JSON);
 *                                              supply via parent that has already
 *                                              loaded the data fixture.
 * @param {string|null} [props.entityCode]   - Region/entity for availability gates.
 */
export function DSCProvider({
  children,
  productLine,
  initialUrlSearch,
  products = [],
  rockDrillManufacturers = null,
  rockDrills = null,
  entityCode = null,
}) {
  // Hydrate initial state: URL params > sessionStorage > defaults.
  const initialState = useMemo(() => {
    const urlHydration = parseUrlHydration(initialUrlSearch);
    const persisted = readPersistedState();

    let line = productLine;
    let toggles = null;
    let selections = null;
    let dropdownChoices = null;

    if (urlHydration && urlHydration.productLine) {
      line = urlHydration.productLine;
      toggles = urlHydration.toggles;
      selections = urlHydration.selections;
    } else if (persisted && persisted.productLine === productLine) {
      toggles = persisted.toggles;
      selections = persisted.selections;
      dropdownChoices = persisted.dropdownChoices;
    }

    const base = makeInitialDSCState(line);
    return {
      ...base,
      toggles: { ...base.toggles, ...(toggles || {}) },
      selections: selections || {},
      dropdownChoices: dropdownChoices || {},
      products,
      entityCode,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only

  const [state, dispatch] = useReducer(dscCascadeReducer, initialState);

  // -------------------------------------------------------------------------
  // Bootstrap master lists + initial dropdown options on mount or productLine
  // change.
  // -------------------------------------------------------------------------
  const [bootstrapKey, setBootstrapKey] = useState(0);
  useEffect(() => {
    if (!products || products.length === 0) return;
    const { filteringData, dthHammerData, initialOptions } = buildDSCBootstrap(
      state.productLine,
      products,
      { unitImperial: !!state.toggles.unitImperial, entityCode }
    );
    dispatch({
      type: DSC_ACTIONS.BOOTSTRAP,
      filteringData,
      dthHammerData,
      initialOptions,
      products, // merged into state.products — fixes the all-tabs-(0) bug.
      rockDrillManufacturers, // for TH shank mfr+model dropdown source
      rockDrills,             // for equipmentMatcher.deriveFromRockDrill
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, rockDrillManufacturers, rockDrills, state.productLine, state.toggles.unitImperial, entityCode, bootstrapKey]);

  // -------------------------------------------------------------------------
  // Persist on every change (debounced via React batching — no explicit timer
  // because writes are cheap and React batches state updates).
  // -------------------------------------------------------------------------
  useEffect(() => {
    writePersistedState(state);
  }, [state.productLine, state.toggles, state.selections, state.dropdownChoices]);

  // -------------------------------------------------------------------------
  // Mirror state into URL (history.replaceState).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    try {
      const qs = buildUrlFromState(state);
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`;
      // Only replace when meaningfully changed — avoid burning history on
      // every keystroke.
      if (newUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        window.history.replaceState(null, '', newUrl);
      }
    } catch {
      // ignore
    }
  }, [state.productLine, state.toggles, state.selections]);

  // -------------------------------------------------------------------------
  // Action helpers
  // -------------------------------------------------------------------------
  const setProductLine = useCallback(
    (line) => dispatch({ type: DSC_ACTIONS.SET_PRODUCT_LINE, value: line }),
    []
  );
  const setToggle = useCallback(
    (toggleKey, value) => dispatch({ type: DSC_ACTIONS.TOGGLE, toggleKey, value: !!value }),
    []
  );
  const setSelection = useCallback(
    (slotId, dropdownKey, value) =>
      dispatch({
        type: DSC_ACTIONS.SET_SELECTION,
        slotId,
        dropdownKey,
        value,
      }),
    []
  );
  const clearSelection = useCallback(
    (slotId) => dispatch({ type: DSC_ACTIONS.CLEAR_SELECTION, slotId }),
    []
  );
  const restart = useCallback(() => {
    dispatch({ type: DSC_ACTIONS.RESTART });
    // Re-trigger bootstrap so initial dropdownOptions are repopulated.
    setBootstrapKey((k) => k + 1);
  }, []);

  // -------------------------------------------------------------------------
  // Derived: tabOrder + tabCounts. The SP1 stub returned zeros; the real
  // counts come from per-tab product filtering (services/dscTabFilter.js)
  // which applies PGMJ+PGMN tuple + cascade-selection narrowing per tab.
  // Recomputes whenever products, toggles, or selections change.
  // -------------------------------------------------------------------------
  const tabOrder = useMemo(
    () => getTabOrder(state.productLine, state.toggles),
    [state.productLine, state.toggles]
  );
  const tabCounts = useMemo(
    () => computeTabCounts(state.products, tabOrder, state.selections, state.dscFilteringData),
    [state.products, tabOrder, state.selections, state.dscFilteringData]
  );

  const slotOrder = useMemo(
    () => getSlotOrder(state.productLine, state.toggles),
    [state.productLine, state.toggles]
  );

  const value = useMemo(
    () => ({
      state,
      dispatch,
      setProductLine,
      setToggle,
      setSelection,
      clearSelection,
      restart,
      tabOrder,
      tabCounts,
      slotOrder,
    }),
    [state, setProductLine, setToggle, setSelection, clearSelection, restart, tabOrder, tabCounts, slotOrder]
  );

  return <DSCContext.Provider value={value}>{children}</DSCContext.Provider>;
}

export default DSCProvider;
